import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DashboardTab = ({ data, t }) => {
    const [vitalSigns, setVitalSigns] = useState({});
    const { t: _t } = useTranslation();
    const tx = t || _t;

    const admissionDate = useMemo(() => {
        if (!data?.visit_date) return '---';
        // supports both "YYYY-MM-DD HH:mm:ss" and ISO strings
        if (typeof data.visit_date === 'string') return data.visit_date.split(' ')[0];
        try {
            return new Date(data.visit_date).toISOString().split('T')[0];
        } catch {
            return '---';
        }
    }, [data?.visit_date]);

    const expectedCheckout = useMemo(() => {
        if (!data?.visit_date || !data?.length_of_stay_days) return tx('common.tbd', { defaultValue: 'TBD' });
        try {
            const date = new Date(data.visit_date);
            date.setDate(date.getDate() + parseInt(data.length_of_stay_days));
            return date.toISOString().split('T')[0];
        } catch {
            return '---';
        }
    }, [data?.visit_date, data?.length_of_stay_days]);

    const assignedDoctor = useMemo(() => {
        const v = data?.vet;
        return v?.full_name || `${v?.first_name || ''} ${v?.last_name || ''}`.trim() || '---';
    }, [data?.vet]);

    const feedingOrder = useMemo(() => {
        const val = data?.feeding_order || data?.allowed_food;
        return val ? tx(`receptionInpatients.admit.fields.feedingOrder.options.${val}`, { defaultValue: val }) : '---';
    }, [data?.feeding_order, data?.allowed_food, tx]);

    const waterOrder = useMemo(() => {
        const val = data?.water_order || data?.allowed_fluids;
        return val ? tx(`receptionInpatients.admit.fields.waterOrder.options.${val}`, { defaultValue: val }) : '---';
    }, [data?.water_order, data?.allowed_fluids, tx]);

    const petAgeDisplay = useMemo(() => {
        const raw = data?.pet?.age;
        if (raw === null || raw === undefined || raw === '') return '---';

        const toYears = (n) => tx('common.ageYears', { count: n, defaultValue: '{{count}} years' });
        const toMonths = (n) => tx('common.ageMonths', { count: n, defaultValue: '{{count}} months' });

        if (typeof raw === 'number') return toYears(raw);

        if (typeof raw === 'string') {
            const numMatch = raw.match(/(\d+)/);
            const n = numMatch ? parseInt(numMatch[1], 10) : NaN;
            const lower = raw.toLowerCase();
            const hasYear = lower.includes('year') || raw.includes('سنة') || raw.includes('سنوات');
            const hasMonth = lower.includes('month') || raw.includes('شهر') || raw.includes('أشهر');
            if (!Number.isNaN(n) && hasYear) return toYears(n);
            if (!Number.isNaN(n) && hasMonth) return toMonths(n);
            return raw;
        }

        return '---';
    }, [data?.pet?.age, tx]);

    const petGenderDisplay = useMemo(() => {
        const raw = data?.pet?.gender;
        if (raw === null || raw === undefined || raw === '') return '---';
        const s = String(raw).trim();
        const k = s.toLowerCase();
        const normalized = (k === 'm' || k === 'male') ? 'male' : (k === 'f' || k === 'female') ? 'female' : null;
        if (!normalized) return s;
        return tx(`completeProfile.gender.options.${normalized}`, { defaultValue: s });
    }, [data?.pet?.gender, tx]);

    const petGenderIcon = useMemo(() => {
        const k = String(data?.pet?.gender || '').trim().toLowerCase();
        if (k === 'm' || k === 'male') return 'fas fa-mars';
        if (k === 'f' || k === 'female') return 'fas fa-venus';
        return 'fas fa-venus-mars';
    }, [data?.pet?.gender]);

    const InfoCard = ({ icon, label, value, valueTone = 'default' }) => {
        const valueNode = valueTone === 'tbd'
            ? <span className="dashboard-value-tbd">{value}</span>
            : value;

        return (
            <div className="info-card-premium info-card-compact">
                <div className="card-icon"><i className={icon}></i></div>
                <div className="card-details">
                    <label>{label}</label>
                    <p>{valueNode}</p>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const fetchLatestVitalSigns = async () => {
            if (!data?.id) return;
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${data.id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Accept': 'application/json'
                    }
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.status === 'success' && result.data && result.data.length > 0) {
                        const latestVitalSigns = result.data[0];

                    }
                }
            } catch (error) {
                console.error('Failed to fetch vital signs for summary:', error);
            }
        };

        fetchLatestVitalSigns();
    }, [data?.id]);

    if (!data) return null;

    return (
        <div className="dashboard-tab-content tab-content-inner dashboard-summary">
            <div className="dashboard-cards-grid">
                {/* Visit Information */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.visitInformation', { defaultValue: 'Visit Information' })}
                </div>
                <InfoCard
                    icon="fas fa-hashtag"
                    label={tx('receptionInpatients.details.labels.serialNumber', { defaultValue: 'Serial Number' })}
                    value={data?.in_serial_num || '---'}
                />
                <InfoCard
                    icon="fas fa-calendar-check"
                    label={tx('receptionInpatients.details.labels.admission', { defaultValue: 'Admission Date' })}
                    value={admissionDate}
                />
                <InfoCard
                    icon="fas fa-hourglass-end"
                    label={tx('receptionInpatients.details.labels.expectedCheckout', { defaultValue: 'Expected Checkout' })}
                    value={expectedCheckout}
                    valueTone={expectedCheckout === tx('common.tbd', { defaultValue: 'TBD' }) ? 'tbd' : 'default'}
                />
                <InfoCard
                    icon="fas fa-user-md"
                    label={tx('receptionInpatients.details.labels.assignedDoctor', { defaultValue: 'Assigned Doctor' })}
                    value={assignedDoctor}
                />

                {/* Pet Information */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.petInformation', { defaultValue: 'Pet Information' })}
                </div>
                <InfoCard
                    icon="fas fa-paw"
                    label={tx('receptionInpatients.details.labels.petName', { defaultValue: 'Pet Name' })}
                    value={data?.pet?.name || '---'}
                />
                <InfoCard
                    icon="fas fa-dna"
                    label={tx('receptionInpatients.details.labels.species', { defaultValue: 'Species' })}
                    value={data?.pet?.species?.name || '---'}
                />
                <InfoCard
                    icon="fas fa-fingerprint"
                    label={tx('receptionInpatients.details.labels.breed', { defaultValue: 'Breed' })}
                    value={data?.pet?.breed?.name || '---'}
                />
                <InfoCard
                    icon="fas fa-birthday-cake"
                    label={tx('receptionInpatients.details.labels.age', { defaultValue: 'Age' })}
                    value={petAgeDisplay}
                />
                <InfoCard
                    icon={petGenderIcon}
                    label={tx('receptionInpatients.details.labels.gender', { defaultValue: 'Gender' })}
                    value={petGenderDisplay}
                />
                <InfoCard
                    icon="fas fa-ban"
                    label={tx('receptionInpatients.details.labels.neutered', { defaultValue: 'Neutered/Spayed' })}
                    value={data?.pet?.neutered
                        ? tx('common.yes', { defaultValue: 'Yes' })
                        : tx('common.no', { defaultValue: 'No' })
                    }
                />

                {/* Guardian Information */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.guardianInformation', { defaultValue: 'Guardian Information' })}
                </div>
                <InfoCard
                    icon="fas fa-user-tie"
                    label={tx('receptionInpatients.details.labels.petGuardian', { defaultValue: 'Pet Guardian' })}
                    value={data?.pet_owner?.full_name || '---'}
                />
                <InfoCard
                    icon="fas fa-phone-alt"
                    label={tx('receptionInpatients.details.labels.contact', { defaultValue: 'Contact Number' })}
                    value={data?.pet_owner?.phone || '---'}
                />

                {/* Other Information */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.otherInformation', { defaultValue: 'Other Information' })}
                </div>
                <InfoCard
                    icon="fas fa-door-closed"
                    label={tx('receptionInpatients.details.labels.cage', { defaultValue: 'Cage' })}
                    value={(data?.cage_no || data?.cage?.name) ? (data?.cage_no || data?.cage?.name) : tx('common.tbd', { defaultValue: 'TBD' })}
                    valueTone={(!data?.cage_no && !data?.cage?.name) ? 'tbd' : 'default'}
                />
                <InfoCard
                    icon="fas fa-user-shield"
                    label={tx('receptionInpatients.details.labels.isolated', { defaultValue: 'Isolated' })}
                    value={(data?.is_isolated === null || data?.is_isolated === undefined)
                        ? tx('common.tbd', { defaultValue: 'TBD' })
                        : (data?.is_isolated ? tx('common.yes', { defaultValue: 'Yes' }) : tx('common.no', { defaultValue: 'No' }))
                    }
                    valueTone={(data?.is_isolated === null || data?.is_isolated === undefined) ? 'tbd' : 'default'}
                />
                <InfoCard
                    icon="fas fa-utensils"
                    label={tx('receptionInpatients.details.labels.feedingOrder', { defaultValue: 'Feeding order' })}
                    value={feedingOrder}
                />
                <InfoCard
                    icon="fas fa-tint"
                    label={tx('receptionInpatients.details.labels.waterOrder', { defaultValue: 'Water order' })}
                    value={waterOrder}
                />
            </div>

        </div>
    );
};

export default DashboardTab;
